<template>
  <div class="settlement-page">
    <section class="page-toolbar">
      <div>
        <strong>供应商结款</strong>
        <span>按月核对会员小鼠供应商应收，登记实际打款后供应商后台会自动更新。</span>
      </div>
      <button class="ghost-button" type="button" :disabled="loading" @click="loadSettlements">
        {{ loading ? "刷新中..." : "刷新数据" }}
      </button>
    </section>

    <section v-if="error" class="state-card state-card--error">{{ error }}</section>
    <section v-else-if="loading && !overview.stores" class="state-card">正在汇总供应商结款...</section>

    <template v-else>
      <section class="summary-grid">
        <article class="summary-card summary-card--pending">
          <span>累计未结款</span>
          <strong>¥{{ formatPrice(overview.totals && overview.totals.unsettledAmount) }}</strong>
          <small>所有供应商尚未打款金额</small>
        </article>
        <article class="summary-card">
          <span>累计已打款</span>
          <strong>¥{{ formatPrice(overview.totals && overview.totals.paidAmount) }}</strong>
          <small>已登记的平台打款流水</small>
        </article>
        <article class="summary-card">
          <span>累计供应商应收</span>
          <strong>¥{{ formatPrice(overview.totals && overview.totals.payableAmount) }}</strong>
          <small>成本价与运费合计</small>
        </article>
      </section>

      <section class="config-card">
        <div class="section-head">
          <div>
            <strong>月度结款明细</strong>
            <span>{{ currentMonthLabel }}，未结金额会在新订单支付后自动增加。</span>
          </div>
          <select v-if="stores.length > 1" v-model.number="selectedStoreId" class="store-select">
            <option v-for="store in stores" :key="store.storeId" :value="store.storeId">
              {{ store.storeName || `店铺 ${store.storeId}` }}
            </option>
          </select>
          <span v-else-if="selectedStore" class="store-name">{{ selectedStore.storeName }}</span>
        </div>

        <div v-if="months.length" class="table-wrap">
          <table class="settlement-table">
            <thead>
              <tr>
                <th>结算月份</th>
                <th>订单数</th>
                <th>供应商应收</th>
                <th>已打款</th>
                <th>未结款</th>
                <th>状态</th>
                <th>最近打款</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="month in months" :key="month.month">
                <td><strong>{{ month.monthLabel }}</strong></td>
                <td>{{ month.orderCount }} 笔</td>
                <td>¥{{ formatPrice(month.payableAmount) }}</td>
                <td>¥{{ formatPrice(month.paidAmount) }}</td>
                <td class="pending-amount">¥{{ formatPrice(month.unsettledAmount) }}</td>
                <td>
                  <em class="status-pill" :class="statusClass(month.status)">{{ month.statusText }}</em>
                </td>
                <td>{{ formatTime(month.lastPaidAt) }}</td>
                <td>
                  <button
                    type="button"
                    class="pay-button"
                    :disabled="Number(month.unsettledAmount || 0) <= 0"
                    @click="openPayment(month)"
                  >
                    登记打款
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="empty-state">暂无供应商结款数据</div>
      </section>

      <section class="config-card payment-history">
        <div class="section-head">
          <div>
            <strong>打款记录</strong>
            <span>保留付款时间、金额、流水号和操作人，方便后续核对。</span>
          </div>
        </div>
        <div v-if="storePayments.length" class="table-wrap">
          <table class="settlement-table">
            <thead>
              <tr>
                <th>打款时间</th>
                <th>结算月份</th>
                <th>打款金额</th>
                <th>方式</th>
                <th>流水号</th>
                <th>备注</th>
                <th>操作人</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="payment in storePayments" :key="payment.id">
                <td>{{ formatTime(payment.paidAt) }}</td>
                <td>{{ monthLabel(payment.month) }}</td>
                <td class="paid-amount">¥{{ formatPrice(payment.amount) }}</td>
                <td>{{ payment.paymentMethod || "-" }}</td>
                <td>{{ payment.paymentReference || "-" }}</td>
                <td>{{ payment.remark || "-" }}</td>
                <td>{{ payment.operator || "-" }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="empty-state">暂无打款记录</div>
      </section>
    </template>

    <div v-if="paymentOpen" class="modal-backdrop" @click.self="closePayment">
      <section class="payment-modal">
        <header>
          <div>
            <span>SUPPLIER PAYMENT</span>
            <h2>登记供应商打款</h2>
            <p>{{ selectedMonthLabel }} · 当前未结 ¥{{ formatPrice(paymentMonth && paymentMonth.unsettledAmount) }}</p>
          </div>
          <button type="button" aria-label="关闭" @click="closePayment">×</button>
        </header>
        <div class="payment-form">
          <label>
            <span>本次打款金额</span>
            <input v-model.number="paymentForm.amount" type="number" min="0.01" :max="paymentMonth && paymentMonth.unsettledAmount" step="0.01">
          </label>
          <label>
            <span>实际打款时间</span>
            <input v-model="paymentForm.paidAt" type="datetime-local">
          </label>
          <label>
            <span>打款方式</span>
            <select v-model="paymentForm.paymentMethod">
              <option value="银行转账">银行转账</option>
              <option value="微信转账">微信转账</option>
              <option value="支付宝转账">支付宝转账</option>
              <option value="其他">其他</option>
            </select>
          </label>
          <label>
            <span>银行流水号</span>
            <input v-model.trim="paymentForm.paymentReference" maxlength="128" placeholder="选填">
          </label>
          <label class="wide">
            <span>备注</span>
            <textarea v-model.trim="paymentForm.remark" maxlength="255" rows="3" placeholder="选填"></textarea>
          </label>
        </div>
        <footer>
          <button class="ghost-button" type="button" :disabled="submitting" @click="closePayment">取消</button>
          <button class="primary-button" type="button" :disabled="submitting" @click="submitPayment">
            {{ submitting ? "登记中..." : "确认已打款" }}
          </button>
        </footer>
      </section>
    </div>

    <div v-if="toast" class="toast" :class="{ 'toast--error': toastError }">{{ toast }}</div>
  </div>
</template>

<script>
import { fetchSupplierSettlements, recordSupplierSettlementPayment } from "../api/adminSystem"

export default {
  name: "SupplierSettlementView",
  data() {
    return {
      loading: false,
      submitting: false,
      error: "",
      overview: {},
      selectedStoreId: 0,
      paymentOpen: false,
      paymentMonth: null,
      paymentForm: {
        amount: 0,
        paidAt: "",
        paymentMethod: "银行转账",
        paymentReference: "",
        remark: ""
      },
      toast: "",
      toastError: false,
      toastTimer: null
    }
  },
  computed: {
    stores() {
      return Array.isArray(this.overview.stores) ? this.overview.stores : []
    },
    selectedStore() {
      return this.stores.find(store => Number(store.storeId) === Number(this.selectedStoreId)) || this.stores[0] || null
    },
    months() {
      return this.selectedStore && Array.isArray(this.selectedStore.months) ? this.selectedStore.months : []
    },
    storePayments() {
      const payments = Array.isArray(this.overview.payments) ? this.overview.payments : []
      return payments.filter(payment => Number(payment.storeId) === Number(this.selectedStoreId))
    },
    currentMonthLabel() {
      return this.monthLabel(this.selectedStore && this.selectedStore.currentMonth)
    },
    selectedMonthLabel() {
      return this.paymentMonth ? this.paymentMonth.monthLabel : "-"
    }
  },
  mounted() {
    this.loadSettlements()
  },
  beforeDestroy() {
    clearTimeout(this.toastTimer)
  },
  methods: {
    async loadSettlements() {
      if (this.loading) return
      this.loading = true
      this.error = ""
      try {
        const response = await fetchSupplierSettlements()
        this.overview = response.data || {}
        if (!this.stores.some(store => Number(store.storeId) === Number(this.selectedStoreId))) {
          this.selectedStoreId = this.stores[0] ? Number(this.stores[0].storeId) : 0
        }
      } catch (error) {
        this.error = error.message || "供应商结款数据加载失败"
      } finally {
        this.loading = false
      }
    },
    openPayment(month) {
      this.paymentMonth = month
      this.paymentForm = {
        amount: Number(month.unsettledAmount || 0),
        paidAt: this.localDateTimeNow(),
        paymentMethod: "银行转账",
        paymentReference: "",
        remark: ""
      }
      this.paymentOpen = true
    },
    closePayment() {
      if (this.submitting) return
      this.paymentOpen = false
      this.paymentMonth = null
    },
    async submitPayment() {
      if (this.submitting || !this.paymentMonth) return
      const remaining = Number(this.paymentMonth.unsettledAmount || 0)
      const amount = Number(this.paymentForm.amount || 0)
      if (!Number.isFinite(amount) || amount <= 0) {
        this.showToast("请输入正确的打款金额", true)
        return
      }
      if (amount > remaining + 0.001) {
        this.showToast("打款金额不能超过当前未结款", true)
        return
      }
      if (!this.paymentForm.paidAt) {
        this.showToast("请选择实际打款时间", true)
        return
      }
      this.submitting = true
      try {
        await recordSupplierSettlementPayment({
          storeId: Number(this.selectedStoreId),
          month: this.paymentMonth.month,
          amount,
          paidAt: this.paymentForm.paidAt,
          paymentMethod: this.paymentForm.paymentMethod,
          paymentReference: this.paymentForm.paymentReference,
          remark: this.paymentForm.remark
        })
        this.paymentOpen = false
        this.paymentMonth = null
        await this.loadSettlements()
        this.showToast("打款已登记，供应商后台已同步")
      } catch (error) {
        this.showToast(error.message || "打款登记失败", true)
      } finally {
        this.submitting = false
      }
    },
    localDateTimeNow() {
      const now = new Date()
      const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000)
      return local.toISOString().slice(0, 16)
    },
    monthLabel(value) {
      if (!value || !/^\d{4}-\d{2}$/.test(String(value))) return "-"
      const [year, month] = String(value).split("-")
      return `${year}年${Number(month)}月`
    },
    statusClass(status) {
      return {
        PAID: "paid",
        PARTIAL: "partial",
        UNPAID: "unpaid",
        NO_BILL: "no-bill"
      }[status] || "no-bill"
    },
    formatPrice(value) {
      return Number(value || 0).toFixed(2)
    },
    formatTime(value) {
      return value ? String(value).replace("T", " ").slice(0, 16) : "-"
    },
    showToast(message, error = false) {
      clearTimeout(this.toastTimer)
      this.toast = message
      this.toastError = error
      this.toastTimer = setTimeout(() => {
        this.toast = ""
      }, 3200)
    }
  }
}
</script>

<style scoped>
.settlement-page {
  max-width: 1380px;
  color: #26323e;
}

.page-toolbar,
.section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.page-toolbar {
  align-items: flex-end;
  margin-bottom: 16px;
}

.page-toolbar strong,
.page-toolbar span,
.section-head strong,
.section-head span {
  display: block;
}

.page-toolbar strong {
  font-size: 18px;
}

.page-toolbar span,
.section-head span {
  margin-top: 6px;
  color: #7b8791;
  font-size: 12px;
  line-height: 18px;
}

.summary-grid {
  display: grid;
  margin-bottom: 14px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}

.summary-card {
  padding: 18px;
  border: 1px solid #e5e7eb;
  border-radius: 7px;
  background: #fff;
}

.summary-card--pending {
  border-color: #fed7aa;
  background: #fffaf5;
}

.summary-card span,
.summary-card strong,
.summary-card small {
  display: block;
}

.summary-card span {
  color: #64748b;
  font-size: 12px;
}

.summary-card strong {
  margin: 10px 0 7px;
  color: #1f2937;
  font-size: 27px;
  line-height: 1;
}

.summary-card--pending strong {
  color: #c2410c;
}

.summary-card small {
  color: #9ca3af;
  font-size: 11px;
}

.config-card {
  margin-bottom: 14px;
  padding: 18px;
  border: 1px solid #e5e7eb;
  border-radius: 7px;
  background: #fff;
}

.store-select {
  width: 220px;
  height: 38px;
  padding: 0 10px;
  border: 1px solid #dde3e8;
  border-radius: 6px;
  background: #fbfcfd;
}

.store-name {
  padding: 7px 11px;
  border-radius: 5px;
  color: #475569 !important;
  background: #f1f5f9;
  font-weight: 700;
}

.table-wrap {
  margin-top: 14px;
  overflow-x: auto;
}

.settlement-table {
  width: 100%;
  min-width: 980px;
  border-collapse: collapse;
}

.settlement-table th,
.settlement-table td {
  padding: 12px 10px;
  border-bottom: 1px solid #eef1f3;
  text-align: left;
  white-space: nowrap;
}

.settlement-table th {
  color: #7a858e;
  background: #fafbfc;
  font-size: 12px;
  font-weight: 600;
}

.settlement-table td {
  color: #4b5563;
  font-size: 13px;
}

.settlement-table tbody tr:hover {
  background: #fafcfb;
}

.pending-amount {
  color: #c2410c !important;
  font-weight: 700;
}

.paid-amount {
  color: #047857 !important;
  font-weight: 700;
}

.status-pill {
  display: inline-flex;
  min-width: 58px;
  padding: 4px 8px;
  justify-content: center;
  border-radius: 999px;
  font-size: 11px;
  font-style: normal;
  font-weight: 700;
}

.status-pill.unpaid {
  color: #b45309;
  background: #fffbeb;
}

.status-pill.partial {
  color: #1d4ed8;
  background: #eff6ff;
}

.status-pill.paid {
  color: #047857;
  background: #ecfdf5;
}

.status-pill.no-bill {
  color: #64748b;
  background: #f1f5f9;
}

.pay-button {
  height: 31px;
  padding: 0 12px;
  border: 1px solid #0f766e;
  border-radius: 4px;
  color: #0f766e;
  background: #f0fdfa;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}

.pay-button:disabled {
  border-color: #e5e7eb;
  color: #b6bec6;
  background: #f8fafc;
  cursor: not-allowed;
}

.payment-history {
  margin-bottom: 0;
}

.modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 30;
  display: grid;
  padding: 24px;
  place-items: center;
  background: rgba(15, 23, 42, 0.48);
}

.payment-modal {
  width: min(620px, 100%);
  overflow: hidden;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 20px 60px rgba(15, 23, 42, 0.24);
}

.payment-modal header {
  display: flex;
  padding: 21px 24px;
  align-items: flex-start;
  justify-content: space-between;
  border-bottom: 1px solid #e5e7eb;
  background: #f8fafc;
}

.payment-modal header span {
  color: #94a3b8;
  font-size: 10px;
  letter-spacing: 1.4px;
}

.payment-modal h2 {
  margin: 7px 0 0;
  color: #111827;
  font-size: 18px;
}

.payment-modal p {
  margin: 7px 0 0;
  color: #64748b;
  font-size: 12px;
}

.payment-modal header button {
  border: 0;
  color: #64748b;
  background: transparent;
  font-size: 24px;
  cursor: pointer;
}

.payment-form {
  display: grid;
  padding: 22px 24px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 15px;
}

.payment-form label {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 7px;
}

.payment-form label.wide {
  grid-column: 1 / -1;
}

.payment-form span {
  color: #55616c;
  font-size: 12px;
  font-weight: 600;
}

.payment-form input,
.payment-form select,
.payment-form textarea {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #dde3e8;
  border-radius: 6px;
  color: #26323e;
  background: #fbfcfd;
  font: inherit;
}

.payment-form input,
.payment-form select {
  height: 39px;
  padding: 0 10px;
}

.payment-form textarea {
  padding: 9px 10px;
  resize: vertical;
}

.payment-modal footer {
  display: flex;
  padding: 0 24px 22px;
  justify-content: flex-end;
  gap: 9px;
}

.toast {
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 40;
  padding: 10px 16px;
  border-radius: 7px;
  color: #fff;
  background: #25313b;
  font-size: 13px;
}

.toast--error {
  background: #a73f3f;
}

@media (max-width: 780px) {
  .page-toolbar,
  .section-head {
    align-items: flex-start;
    flex-direction: column;
  }

  .summary-grid {
    grid-template-columns: 1fr;
  }

  .payment-form {
    grid-template-columns: 1fr;
  }

  .payment-form label.wide {
    grid-column: auto;
  }
}
</style>
